/**
 * WebAuthn PRF wrapper for biometric wallet unlock.
 *
 * On iOS 17+ (Safari 17+) and Chrome 116+ the WebAuthn `prf` extension
 * lets us derive a stable per-credential 32-byte secret from a Face ID /
 * Touch ID prompt, without ever seeing the user's biometric data. We
 * use that secret to wrap the seed-encryption key (see
 * packages/crypto/src/biometric.ts).
 *
 * Browser feature detection: `enrollBiometric` checks for the prf
 * extension in the credential-creation result and rejects if the
 * authenticator didn't support it. The Settings UI guards itself with
 * `isWebAuthnPrfSupported()` first to hide the toggle on old browsers.
 */

const RP_NAME = 'GonkaHub Wallet';
// Tied to the eTLD+1 of wallet.gonkahub.com. Passkeys are scoped to
// this domain, so only the wallet origin can authenticate them.
const RP_ID = window.location.hostname;

interface PrfExtensionResults {
  prf?: {
    enabled?: boolean;
    results?: {
      first?: BufferSource;
    };
  };
}

function bufferToBase64Url(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
}

function base64UrlToBytes(str: string): Uint8Array {
  const pad = str.length % 4 === 0 ? '' : '='.repeat(4 - (str.length % 4));
  const b64 = str.replaceAll('-', '+').replaceAll('_', '/') + pad;
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Quick capability check. WebAuthn itself is widely available, but
 * the PRF extension is much newer. We can't truly know without
 * actually trying to create a credential, so this is best-effort:
 * returns false if WebAuthn isn't available at all.
 */
export function isWebAuthnAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator.credentials?.create === 'function'
  );
}

export interface BiometricEnrollmentResult {
  readonly credentialId: string; // base64url
  readonly prfSecret: Uint8Array; // 32 bytes from PRF.first
  readonly prfSalt: Uint8Array; // the 32-byte salt we used
}

/**
 * Create a new passkey for biometric unlock and immediately exercise
 * the PRF extension to retrieve a 32-byte secret. The Settings UI
 * passes that secret into wrapKeyWithPrf().
 *
 * Throws if the user cancels Face ID, the device doesn't support PRF,
 * or WebAuthn isn't available.
 */
export async function enrollBiometric(
  walletId: string,
  walletName: string,
  prfSalt: Uint8Array,
): Promise<BiometricEnrollmentResult> {
  if (!isWebAuthnAvailable()) {
    throw new Error('WebAuthn is not available in this browser');
  }
  if (prfSalt.length !== 32) {
    throw new Error('prfSalt must be 32 bytes');
  }

  // The user-id binds the passkey to this wallet. We use the wallet
  // UUID (16 bytes after stripping dashes) so creating a fresh wallet
  // gives a fresh passkey.
  const userIdHex = walletId.replaceAll('-', '');
  const userId = new Uint8Array(userIdHex.length / 2);
  for (let i = 0; i < userId.length; i++) {
    userId[i] = parseInt(userIdHex.slice(i * 2, i * 2 + 2), 16);
  }

  const challenge = crypto.getRandomValues(new Uint8Array(32));

  const cred = (await navigator.credentials.create({
    publicKey: {
      rp: { name: RP_NAME, id: RP_ID },
      user: {
        id: userId as BufferSource,
        name: walletName,
        displayName: walletName,
      },
      challenge: challenge as BufferSource,
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 }, // ES256
        { type: 'public-key', alg: -257 }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'preferred',
      },
      timeout: 60_000,
      attestation: 'none',
      extensions: {
        // Ask for PRF eval at creation time. Safari 17 returns the PRF
        // result on the create() call directly; older WebAuthn impls
        // need a follow-up get(). We try create-time first.
        prf: {
          eval: {
            first: prfSalt as BufferSource,
          },
        },
      } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!cred) {
    throw new Error('Credential creation returned null');
  }

  const credentialId = bufferToBase64Url(cred.rawId);
  const ext = (cred.getClientExtensionResults() as unknown as PrfExtensionResults).prf;

  // Path 1: PRF result returned directly on create() (Safari 17+ on iOS).
  if (ext?.results?.first) {
    const prfSecret = new Uint8Array(
      ext.results.first instanceof ArrayBuffer
        ? ext.results.first
        : (ext.results.first as Uint8Array).buffer,
    );
    if (prfSecret.length !== 32) {
      throw new Error(`PRF returned ${prfSecret.length} bytes, expected 32`);
    }
    return { credentialId, prfSecret, prfSalt };
  }

  // Path 2: authenticator only acknowledged PRF support — we have to
  // run get() once to actually retrieve the secret. This is the
  // Chrome / Android path.
  if (ext?.enabled) {
    const result = await authenticateBiometric(credentialId, prfSalt);
    return { credentialId, prfSecret: result.prfSecret, prfSalt };
  }

  throw new Error(
    'This authenticator does not support the PRF extension. ' +
      'Biometric unlock is not available on this device.',
  );
}

export interface BiometricAuthResult {
  readonly prfSecret: Uint8Array;
}

/**
 * Authenticate against an existing passkey to retrieve the same 32-byte
 * PRF secret. The caller is responsible for taking that secret into
 * unwrapKeyWithPrf() and zeroizing it after use.
 */
export async function authenticateBiometric(
  credentialIdB64: string,
  prfSalt: Uint8Array,
): Promise<BiometricAuthResult> {
  if (!isWebAuthnAvailable()) {
    throw new Error('WebAuthn is not available in this browser');
  }
  if (prfSalt.length !== 32) {
    throw new Error('prfSalt must be 32 bytes');
  }
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const credentialIdBytes = base64UrlToBytes(credentialIdB64);

  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: challenge as BufferSource,
      rpId: RP_ID,
      allowCredentials: [
        {
          type: 'public-key',
          id: credentialIdBytes as BufferSource,
          transports: ['internal'],
        },
      ],
      userVerification: 'required',
      timeout: 60_000,
      extensions: {
        prf: {
          eval: {
            first: prfSalt as BufferSource,
          },
        },
      } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!assertion) {
    throw new Error('Biometric authentication returned null');
  }

  const ext = (assertion.getClientExtensionResults() as unknown as PrfExtensionResults).prf;
  if (!ext?.results?.first) {
    throw new Error('PRF extension did not return a secret');
  }
  const prfSecret = new Uint8Array(
    ext.results.first instanceof ArrayBuffer
      ? ext.results.first
      : (ext.results.first as Uint8Array).buffer,
  );
  if (prfSecret.length !== 32) {
    throw new Error(`PRF returned ${prfSecret.length} bytes, expected 32`);
  }
  return { prfSecret };
}
